import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  COLOR_TOKENS,
  type ColorToken,
  type ConcentricRing,
  ConcentricRingChart,
  DonutChart,
  type DonutSegment,
  type HeatColumn,
  HeatGrid,
  type HeatRow,
  HorizontalMenuTabs,
  LabeledFilterSelect,
  LegendRow,
  useContainerQuery,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type AcademicTab,
  type AyotResponse,
  type CareerTrack,
  type MeasureBins,
  type MeasureId,
  type RigorBand,
  type TrackCounts,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

import {
  alignmentLabel,
  bucketCount,
  measureLabel,
  percentOf,
  percentShares,
  unlockStatusLabel,
} from './analytics.labels';
import { donutWrapSx, mutedTextSx, titleSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { SubsectionHeader } from './AnalyticsSubsectionHeader';
import { useAgentContext } from './OnTrackCards.agentContext';
import { OnTrackHeatMapCard } from './OnTrackHeatMapCard';
import { type SegmentFilter } from './StudentDataModal';
import { useStudentData } from './useStudentData';
import { COLUMNS, COMMON_COLUMNS, unlockStatusColumns } from './viewData.columns';

const { onTrack, hover, viewData } = appStrings.modules.coachingAnalytics;

const ASPIRATION_COLUMNS = [
  ...COMMON_COLUMNS,
  COLUMNS.careerTrack,
  COLUMNS.assessedTrack,
  COLUMNS.trackAlignment,
];

const ACADEMIC_COLUMNS = [
  ...COMMON_COLUMNS,
  COLUMNS.assessedTrack,
  COLUMNS.gpa,
  COLUMNS.sat,
  COLUMNS.act,
  COLUMNS.courseworkRigor,
  COLUMNS.majorRigor,
  COLUMNS.schoolSelectivity,
];

// Three steps of the heat grid's own scale, so the two share a colour language.
const ALIGNMENT_COLORS: Readonly<Record<string, ColorToken>> = {
  ahead: 'Brand/600',
  aligned: 'Brand/400',
  behind: 'Brand/200',
};

type UnlockStatusId = 'on_track' | 'developing' | 'not_yet';

const UNLOCK_COLORS: Readonly<Record<UnlockStatusId, ColorToken>> = {
  on_track: 'Success/400',
  developing: 'Warning/400',
  not_yet: 'Orange/400',
};

const UNLOCK_ORDER: readonly UnlockStatusId[] = ['on_track', 'developing', 'not_yet'];

/** Lightest to darkest, so the bar reads as a ladder from semi-skilled to top. */
const TRACK_COLORS: readonly ColorToken[] = [
  'Brand/200',
  'Brand/400',
  'Brand/600',
  'Brand/800',
  'Brand/950',
];

const DONUT_SIZE = 168;
const RING_SIZE = 176;

// The bubble rows and the axis beneath them share this gutter, so they must move together.
const BUBBLE_LABEL_WIDTH = 112;

// Track ids are plain strings, so the lookup widens the keyed map.
const SHORTEST_TRACK_NAMES: Readonly<Record<string, string | undefined>> =
  appStrings.common.careerTrackShortestNames;

const trackLabel = (track: CareerTrack, isCompact: boolean): string =>
  (isCompact ? SHORTEST_TRACK_NAMES[track.id] : undefined) ?? track.name;

/** Both axes run lowest track first, so cells above the diagonal are reach exceeding aim. */
const trackColumns = (tracks: readonly CareerTrack[], isCompact: boolean): readonly HeatColumn[] =>
  [...tracks]
    .sort((a, b) => a.rank - b.rank)
    .map((track) => ({ id: track.id, label: trackLabel(track, isCompact) }));

const AspirationCard = ({ data }: Readonly<{ data: AyotResponse }>) => {
  // The matrix keeps its own column, which is a fraction of the page on wide screens.
  const [matrixRef, isCompact] = useContainerQuery(650);
  const { aspiration_vs_reality: aspiration, tracks } = data;
  const columns = trackColumns(tracks, isCompact);
  const trackName = new Map(tracks.map((track) => [track.id, trackLabel(track, isCompact)]));

  const shares = percentShares(
    aspiration.statuses.map((status) => status.count),
    aspiration.total,
  );
  const legend = aspiration.statuses.map((status, index) => ({
    id: status.id,
    label: alignmentLabel(status.id),
    color: ALIGNMENT_COLORS[status.id] ?? 'Gray/200',
    pct: shares[index],
  }));

  const segments: DonutSegment[] = legend.map((item) => ({
    label: item.label,
    value: item.pct,
    color: item.color,
  }));

  const rows: readonly HeatRow[] = columns.map((column) => {
    const row = aspiration.matrix.aspirations.find((entry) => entry.aspiration === column.id);

    return { id: column.id, label: column.label, counts: row?.tracks ?? {} };
  });

  const trackRank = new Map(tracks.map((track) => [track.id, track.rank]));
  const cellTooltip = (aspirationId: string, reachId: string, count: number): string => {
    const { matrixCell } = onTrack.aspiration;
    const aspirationName = trackName.get(aspirationId) ?? aspirationId;
    const reachName = trackName.get(reachId) ?? reachId;
    const aspirationRank = trackRank.get(aspirationId) ?? 0;
    const reachRank = trackRank.get(reachId) ?? 0;

    if (reachRank === aspirationRank) return matrixCell.aligned(count, aspirationName);

    return reachRank < aspirationRank
      ? matrixCell.behind(count, aspirationName, reachName)
      : matrixCell.ahead(count, aspirationName, reachName);
  };

  const alignmentStudents = useStudentData(
    'coaching-analytics-card-aspiration',
    onTrack.aspiration.title,
    ASPIRATION_COLUMNS,
  );
  const matrixStudents = useStudentData(
    'coaching-analytics-card-aspiration-matrix',
    onTrack.aspiration.matrixTitle,
    ASPIRATION_COLUMNS,
  );

  const selectAlignment = (index: number) =>
    alignmentStudents.openSegment({
      label: legend[index].label,
      where: [{ field: 'track_alignment', op: 'eq', value: legend[index].id }],
    });

  const selectCell = (aspirationId: string, reachId: string) =>
    matrixStudents.openSegment({
      label: viewData.aspirationReach(
        trackName.get(aspirationId) ?? aspirationId,
        trackName.get(reachId) ?? reachId,
      ),
      where: [
        { field: 'aspiration_track', op: 'eq', value: aspirationId },
        { field: 'assessed_track', op: 'eq', value: reachId },
      ],
    });

  const info = [
    onTrack.aspiration.caveat,
    aspiration.excluded.no_blueprint > 0 && onTrack.excluded(aspiration.excluded.no_blueprint),
  ];

  return (
    <Box sx={aspirationRowSx}>
      <AnalyticsCard
        title={onTrack.aspiration.title}
        subtitle={onTrack.aspiration.subtitle}
        info={info}
        viewData={alignmentStudents}
      >
        <Box sx={donutColumnSx}>
          <Box sx={donutWrapSx}>
            <DonutChart
              id="coaching-analytics-alignment-donut"
              variant={{ type: 'multi', segments }}
              size={DONUT_SIZE}
              onSegmentClick={selectAlignment}
            />
          </Box>
          <Box sx={legendRowInlineSx}>
            {legend.map((item, index) => (
              <LegendRow
                key={item.id}
                id={`coaching-analytics-aspiration-legend-${item.id}`}
                color={item.color}
                label={item.label}
                pct={item.pct}
                onClick={() => selectAlignment(index)}
              />
            ))}
          </Box>
        </Box>
      </AnalyticsCard>
      <AnalyticsCard
        title={onTrack.aspiration.matrixTitle}
        subtitle={onTrack.aspiration.matrixSubtitle}
        info={[onTrack.aspiration.matrixInfo, ...info]}
        viewData={matrixStudents}
      >
        <Box sx={matrixColumnSx}>
          <Box sx={matrixGridSx} ref={matrixRef}>
            <HeatGrid
              id="coaching-analytics-aspiration-cell"
              density="comfortable"
              rows={rows}
              columns={columns}
              xAxisLabel={onTrack.aspiration.matrixXAxis}
              yAxisLabel={onTrack.aspiration.matrixYAxis}
              isHighlighted={(rowId, columnId) => rowId === columnId}
              cellTooltip={cellTooltip}
              onCellClick={selectCell}
            />
          </Box>
        </Box>
      </AnalyticsCard>
    </Box>
  );
};

/** One row of a distribution chart: a band, its size, and its max-track split. */
type ChartRow = Readonly<{
  key: string;
  label: string;
  total: number;
  tracks: TrackCounts;
}>;

type ChartSpec = Readonly<{
  id: string;
  title: string;
  sample: number;
  rows: readonly ChartRow[];
  note?: string;
  empty?: string;
}>;

const bandRows = (bands: readonly RigorBand[]): readonly ChartRow[] =>
  bands.map((band) => ({
    key: String(band.id),
    label: band.name,
    total: band.total,
    tracks: band.tracks,
  }));

// Only bands holding a student come back, so the chart is already trimmed to
// the occupied range.
const measureChart = (measureId: string, measure: MeasureBins): ChartSpec => ({
  id: measureId,
  title: measureLabel(measureId),
  sample: measure.with_score,
  rows: measure.bins.map((bin) => ({
    key: String(bin.min),
    label: `${bin.min}–${bin.max}`,
    total: bin.total,
    tracks: bin.tracks,
  })),
  note:
    measure.no_score_on_file > 0
      ? onTrack.academicProfile.noScore(measure.no_score_on_file)
      : undefined,
  empty: onTrack.academicProfile.noScores,
});

const rigorNote = (tab: AcademicTab): string => {
  const note =
    tab.rigor.type === 'coursework'
      ? onTrack.academicProfile.courseworkRigorNote
      : onTrack.academicProfile.majorRigorNote;

  return tab.rigor.not_on_file > 0
    ? `${note} · ${onTrack.academicProfile.notOnFile(tab.rigor.not_on_file)}`
    : note;
};

const rigorChart = (tab: AcademicTab): ChartSpec => ({
  id: 'rigor',
  title:
    tab.rigor.type === 'coursework'
      ? onTrack.academicProfile.courseworkRigor
      : onTrack.academicProfile.majorRigor,
  sample: tab.total,
  rows: bandRows(tab.rigor.bands),
  note: rigorNote(tab),
});

const selectivityChart = (tab: AcademicTab): readonly ChartSpec[] =>
  tab.selectivity
    ? [
        {
          id: 'selectivity',
          title: onTrack.academicProfile.collegeSelectivity,
          sample: tab.total,
          rows: bandRows(tab.selectivity.bands),
          note: onTrack.academicProfile.selectivityNote,
        },
      ]
    : [];

/**
 * High school reads GPA, coursework rigor, SAT, ACT as a 2×2; post-secondary
 * drops the tests and adds college selectivity below.
 */
const academicCharts = (tab: AcademicTab): readonly ChartSpec[] => {
  const measure = (id: MeasureId): readonly ChartSpec[] => {
    const bins = tab.measures[id];

    return bins ? [measureChart(id, bins)] : [];
  };

  return [
    ...measure('gpa'),
    rigorChart(tab),
    ...measure('sat'),
    ...measure('act'),
    ...selectivityChart(tab),
  ];
};

type TrackColors = ReadonlyMap<string, ColorToken>;

/** Ascending rank, so the lightest colour is the lowest track. */
const trackColors = (tracks: readonly CareerTrack[]): TrackColors =>
  new Map(
    [...tracks]
      .sort((a, b) => a.rank - b.rank)
      .map((track, index) => [track.id, TRACK_COLORS[Math.min(index, TRACK_COLORS.length - 1)]]),
  );

/** A cell of the bubble grid: the students in one band who reach one track. */
type BubbleCell = Readonly<{
  key: string;
  count: number;
  color: ColorToken;
  title: string;
}>;

const MIN_BUBBLE = 26;
const MAX_BUBBLE = 52;

/** Below this a two- or three-digit count no longer fits inside the bubble. */
const COUNT_LEGIBLE_BUBBLE = 22;

/** Roughly what a band label ("1200-1250") needs before neighbours collide. */
const MIN_TICK_WIDTH = 48;

/** Keeps neighbouring bubbles from touching at their widest. */
const BUBBLE_GUTTER = 4;

/** Area-proportional, floored and capped so a bubble never outgrows its band. */
const bubbleSize = (count: number, peak: number, ceiling: number): number => {
  const max = Math.min(MAX_BUBBLE, ceiling);
  // A fixed floor would flatten every bubble to one size once the ceiling drops below it.
  const min = Math.min(MIN_BUBBLE, max * (MIN_BUBBLE / MAX_BUBBLE));

  return min + (max - min) * Math.sqrt(count / peak);
};

/** Width of one band column, or null until the chart has been measured. */
const useBandWidth = (bandCount: number) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  // The axis only exists once there are bands, so the observer waits for it.
  const hasBands = bandCount > 0;

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasBands]);

  return [ref, width === null || bandCount === 0 ? null : width / bandCount] as const;
};

type BubbleChartProps = Readonly<{
  chart: ChartSpec;
  tracks: readonly CareerTrack[];
  colors: TrackColors;
}>;

/** Bands across, track reached up, one bubble per pairing sized by how many students. */
const AcademicBubbleChart = ({ chart, tracks, colors }: BubbleChartProps) => {
  const [axisRef, bandWidth] = useBandWidth(chart.rows.length);
  const descending = [...tracks].sort((a, b) => b.rank - a.rank);

  const bubbleCeiling = bandWidth === null ? MAX_BUBBLE : bandWidth - BUBBLE_GUTTER;
  const tickStride = bandWidth === null ? 1 : Math.max(1, Math.ceil(MIN_TICK_WIDTH / bandWidth));

  const untracked = (row: ChartRow): number =>
    row.total - tracks.reduce((sum, track) => sum + (row.tracks[track.id] ?? 0), 0);

  const rows: readonly Readonly<{ id: string; name: string; color: ColorToken }>[] = [
    ...descending.map((track) => ({
      id: track.id,
      name: track.name,
      color: colors.get(track.id) ?? 'Brand/600',
    })),
    ...(chart.rows.some((row) => untracked(row) > 0)
      ? [
          {
            id: 'no_track',
            name: onTrack.academicProfile.noTrack,
            color: 'Gray/300' as ColorToken,
          },
        ]
      : []),
  ];

  const cellCount = (row: ChartRow, trackId: string): number =>
    trackId === 'no_track' ? untracked(row) : (row.tracks[trackId] ?? 0);

  const bubbleTitle = (row: ChartRow, track: { id: string; name: string }): string => {
    const count = cellCount(row, track.id);
    const band = `${chart.title} ${row.label}`;
    const splits = rows.filter((other) => cellCount(row, other.id) > 0).length > 1;

    return splits
      ? onTrack.academicProfile.bubbleShare(
          band,
          track.name,
          count,
          row.total,
          percentOf(count, row.total),
        )
      : onTrack.academicProfile.bubbleCount(band, track.name, count);
  };

  const peak = Math.max(
    ...chart.rows.flatMap((row) => rows.map((track) => cellCount(row, track.id))),
    1,
  );

  if (chart.rows.length === 0) {
    return (
      <Typography variant="text-sm-regular" sx={mutedTextSx}>
        {chart.empty ?? onTrack.academicProfile.noScores}
      </Typography>
    );
  }

  return (
    <Box sx={bubbleChartSx}>
      {rows.map((track) => (
        <Box key={track.id} sx={bubbleRowSx}>
          <Typography variant="text-xs-medium" sx={bubbleRowLabelSx}>
            {track.name}
          </Typography>
          <Box sx={bubbleCellsSx}>
            {chart.rows.map((row) => {
              const cell: BubbleCell = {
                key: row.key,
                count: cellCount(row, track.id),
                color: track.color,
                title: bubbleTitle(row, track),
              };

              const size = bubbleSize(cell.count, peak, bubbleCeiling);

              return (
                <Box key={cell.key} sx={bubbleCellSx}>
                  {cell.count > 0 ? (
                    <Tooltip arrow placement="top" title={cell.title}>
                      <Box
                        sx={bubbleSx}
                        style={{
                          width: size,
                          height: size,
                          backgroundColor: COLOR_TOKENS[cell.color],
                        }}
                      >
                        {size >= COUNT_LEGIBLE_BUBBLE ? (
                          <Typography variant="text-xs-semibold" sx={bubbleValueSx(cell.color)}>
                            {cell.count}
                          </Typography>
                        ) : null}
                      </Box>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
      <Box sx={bubbleAxisSx} ref={axisRef}>
        {chart.rows.map((row, index) => (
          <Typography key={row.key} variant="text-xs-medium" sx={bubbleAxisLabelSx}>
            {index % tickStride === 0 ? row.label : ''}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const AcademicProfileCard = ({ data }: Readonly<{ data: AyotResponse }>) => {
  const [levelIndex, setLevelIndex] = useState(0);
  const [measureId, setMeasureId] = useState('gpa');

  const { academic_profile: profile, tracks } = data;
  const tab = levelIndex === 0 ? profile.high_school : profile.post_secondary;
  const colors = trackColors(tracks);
  const ascending = [...tracks].sort((a, b) => a.rank - b.rank);

  const charts = academicCharts(tab);
  const chart = charts.find((option) => option.id === measureId) ?? charts[0];
  const hasChart = tab.total > 0 && Boolean(chart);

  const info = [
    onTrack.academicProfile.description,
    chart?.note,
    profile.unclassified > 0 && onTrack.academicProfile.unclassified(profile.unclassified),
  ];

  const students = useStudentData(
    'coaching-analytics-card-academic-profile',
    onTrack.academicProfile.title,
    ACADEMIC_COLUMNS,
  );

  return (
    <AnalyticsCard
      title={onTrack.academicProfile.title}
      subtitle={onTrack.academicProfile.subtitle}
      info={info}
      viewData={students}
      belowHeader={
        <Box sx={academicHeaderSx}>
          <Box sx={academicControlsSx}>
            <HorizontalMenuTabs
              id="coaching-analytics-academic-level-tabs"
              variant="filter"
              tabs={[
                onTrack.academicProfile.levelTab(
                  onTrack.academicProfile.highSchool,
                  profile.high_school.total,
                ),
                onTrack.academicProfile.levelTab(
                  onTrack.academicProfile.postSecondary,
                  profile.post_secondary.total,
                ),
              ]}
              value={levelIndex}
              setValue={(next) => {
                setLevelIndex(next);
                setMeasureId('gpa');
              }}
            />
            <Box sx={measureFieldSx}>
              <LabeledFilterSelect
                id="coaching-analytics-academic-measure"
                label={onTrack.academicProfile.measure}
                value={chart?.id ?? ''}
                options={charts.map((option) => ({ value: option.id, label: option.title }))}
                onChange={setMeasureId}
              />
            </Box>
          </Box>
          {hasChart ? (
            <Box sx={trackLegendSx}>
              <Typography variant="text-xs-medium" sx={mutedTextSx}>
                {onTrack.academicProfile.trackReached}
              </Typography>
              {ascending.map((track) => (
                <LegendRow
                  key={track.id}
                  color={colors.get(track.id) ?? 'Brand/600'}
                  label={track.name}
                />
              ))}
              <Typography variant="text-xs-medium" sx={sampleSizeSx}>
                {onTrack.academicProfile.sampleSize(chart.sample)}
              </Typography>
            </Box>
          ) : null}
        </Box>
      }
    >
      {hasChart ? (
        <AcademicBubbleChart chart={chart} tracks={tracks} colors={colors} />
      ) : (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {onTrack.academicProfile.noStudents}
        </Typography>
      )}
    </AnalyticsCard>
  );
};

const NonAcademicCard = ({ data }: Readonly<{ data: AyotResponse }>) => {
  const profile = data.non_academic_profile;

  const shares = percentShares(
    UNLOCK_ORDER.map((id) => bucketCount(profile.statuses, id)),
    profile.total,
  );

  const legend = UNLOCK_ORDER.map((id, index) => ({
    id,
    label: unlockStatusLabel(id),
    color: UNLOCK_COLORS[id],
    pct: shares[index],
  }));

  // The verdict ignores Academic Foundations, and so does this chart.
  const categories = profile.categories.filter((category) => category.counts_toward_verdict);

  const columns = useMemo(
    () => [
      ...COMMON_COLUMNS,
      COLUMNS.profileStrength,
      ...unlockStatusColumns(
        categories.map((category) => ({ key: category.id, name: category.name })),
      ),
    ],
    [categories],
  );
  const students = useStudentData(
    'coaching-analytics-card-non-academic-profile',
    onTrack.onTrackStatus.title,
    columns,
  );

  const selectVerdict = (id: UnlockStatusId): SegmentFilter => ({
    label: unlockStatusLabel(id),
    where: [{ field: 'profile_strength', op: 'eq', value: id }],
  });

  const rings: readonly ConcentricRing[] = legend.map((item) => ({
    id: `coaching-analytics-verdict-ring-${item.id}`,
    pct: item.pct,
    color: item.color,
    tooltip: hover.percent(item.label, item.pct),
    onClick: () => students.openSegment(selectVerdict(item.id)),
  }));

  return (
    <AnalyticsCard
      title={onTrack.onTrackStatus.title}
      subtitle={onTrack.onTrackStatus.subtitle(profile.total)}
      info={[
        onTrack.statusByUnlock.nonAcademicCategories,
        profile.excluded.no_blueprint > 0 && onTrack.excluded(profile.excluded.no_blueprint),
      ]}
      viewData={students}
    >
      <Box sx={splitRowSx}>
        <Box sx={unlockRingColumnSx}>
          <ConcentricRingChart
            rings={rings}
            size={RING_SIZE}
            centerContent={
              <Typography variant="display-sm-semibold" sx={titleSx}>
                {profile.total.toLocaleString()}
              </Typography>
            }
          />
          <Box sx={legendColumnSx}>
            {legend.map((item) => (
              <LegendRow
                key={item.id}
                id={`coaching-analytics-verdict-legend-${item.id}`}
                color={item.color}
                label={item.label}
                pct={item.pct}
                onClick={() => students.openSegment(selectVerdict(item.id))}
              />
            ))}
          </Box>
        </Box>
        <Box sx={matrixColumnSx}>
          <Typography variant="text-sm-semibold">{onTrack.statusByUnlock.title}</Typography>
          <Box sx={unlockBarsRowSx}>
            {categories.map((category) => {
              // Shares rather than independent rounding, so the three segments
              // fill the fixed-height bar exactly.
              const heights = percentShares(
                UNLOCK_ORDER.map((id) => bucketCount(category.statuses, id)),
                category.total,
              );
              const lastVisibleIndex = heights.reduce(
                (last, height, index) => (height > 0 ? index : last),
                -1,
              );

              return (
                <Box key={category.id} sx={unlockColumnSx}>
                  <Box sx={unlockBarSx}>
                    {UNLOCK_ORDER.map((id, index) => (
                      <Tooltip
                        key={id}
                        arrow
                        placement="top"
                        title={hover.share(
                          unlockStatusLabel(id),
                          bucketCount(category.statuses, id),
                          percentOf(bucketCount(category.statuses, id), category.total),
                        )}
                      >
                        <Box
                          id={`coaching-analytics-unlock-${category.id}-${id}`}
                          sx={unlockSegmentSx(
                            index === 0,
                            index === UNLOCK_ORDER.length - 1,
                            heights[index] > 0 && index < lastVisibleIndex,
                          )}
                          style={{
                            height: `${heights[index]}%`,
                            backgroundColor: COLOR_TOKENS[UNLOCK_COLORS[id]],
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            students.openSegment({
                              label: viewData.segmentLabel(category.name, unlockStatusLabel(id)),
                              where: [
                                {
                                  field: 'unlock_status_by_category',
                                  key: category.id,
                                  op: 'eq',
                                  value: id,
                                },
                              ],
                            });
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography variant="text-xs-medium" sx={unlockLabelSx}>
                    {category.shortname ?? category.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box sx={legendRowCenteredSx}>
            {legend.map((item) => (
              <LegendRow key={item.id} color={item.color} label={item.label} />
            ))}
          </Box>
        </Box>
      </Box>
    </AnalyticsCard>
  );
};

export const OnTrackSection = ({
  data,
  action,
  detail = false,
}: Readonly<{ data: AyotResponse; action?: ReactNode; detail?: boolean }>) => {
  useAgentContext(data, detail);

  return (
    <Box sx={sectionSx}>
      <SubsectionHeader
        toolId="are_you_on_track"
        title={onTrack.title}
        subtitle={onTrack.subtitle}
        action={
          action ?? (
            <LubavButton
              id="coaching-analytics-on-track-discuss-lubav"
              label={appStrings.modules.coachingAnalytics.successSystem.discussWithLubav}
              promptButtonId="coaching_analytics_ayot"
            />
          )
        }
      />
      <AspirationCard data={data} />
      <AcademicProfileCard data={data} />
      <NonAcademicCard data={data} />
      {detail && <OnTrackHeatMapCard map={data.heat_map} />}
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const aspirationRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  width: '100%',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'minmax(0, 0.7fr) minmax(0, 1.3fr)',
  },
});

const splitRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'flex-start',
  width: '100%',
  '@container (min-width: 650px)': {
    gridTemplateColumns: 'minmax(0, 0.7fr) minmax(0, 1.3fr)',
  },
});

const donutColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const unlockRingColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 650px)': {
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xl'],
  },
});

const legendColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const legendRowInlineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const legendRowCenteredSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const matrixColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  minWidth: 0,
  width: '100%',
});

const matrixGridSx: SxProps<Theme> = {
  containerType: 'inline-size',
  minWidth: 0,
  width: '100%',
};

const academicHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const trackLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const academicControlsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const measureFieldSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 200,
};

const sampleSizeSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  marginLeft: 'auto',
});

const bubbleChartSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
};

/** Room for the largest bubble plus breathing space above and below it. */
const BUBBLE_ROW_HEIGHT = 64;

const bubbleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  height: BUBBLE_ROW_HEIGHT,
  width: '100%',
});

// The track legend above the chart carries the colours, so a narrow column drops these.
const bubbleRowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'none',
  color: backrs.colors['Colors/Text/text-secondary'],
  flexShrink: 0,
  textAlign: 'right',
  '@container (min-width: 650px)': {
    display: 'block',
    width: BUBBLE_LABEL_WIDTH,
  },
});

const bubbleCellsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  minWidth: 0,
  height: '100%',
  borderLeft: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const bubbleCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  minWidth: 0,
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: `1px dotted ${backrs.colors['Colors/Border/border-secondary']}`,
});

const bubbleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  flexShrink: 0,
});

/** The two lightest track colours need dark text to stay readable. */
const bubbleValueSx =
  (color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    color:
      color === 'Brand/200' || color === 'Brand/400' || color === 'Gray/300'
        ? backrs.colors['Colors/Text/text-primary']
        : backrs.colors['Colors/Text/text-white'],
  });

const bubbleAxisSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: '100%',
  paddingTop: backrs.spacing['spacing-sm'],
  '@container (min-width: 650px)': {
    paddingLeft: `calc(${BUBBLE_LABEL_WIDTH}px + ${backrs.spacing['spacing-md']})`,
  },
});

const bubbleAxisLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  flex: 1,
  minWidth: 0,
  textAlign: 'center',
});

const unlockBarsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const unlockColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  minWidth: 0,
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const unlockBarSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: 72,
  height: 200,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
});

const unlockSegmentSx =
  (isFirst: boolean, isLast: boolean, hasSeparator: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    cursor: 'pointer',
    borderBottom: hasSeparator ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
    borderTopLeftRadius: isFirst ? backrs.radius['radius-md'] : 0,
    borderTopRightRadius: isFirst ? backrs.radius['radius-md'] : 0,
    borderBottomLeftRadius: isLast ? backrs.radius['radius-md'] : 0,
    borderBottomRightRadius: isLast ? backrs.radius['radius-md'] : 0,
  });

const unlockLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  textAlign: 'center',
});
